/**
 * Bridge between the messenger UI's `Conversation` shape and the presence
 * store. Until conversations carry real iam.users UUIDs, this gracefully
 * falls back to the legacy `Conversation.isOnline` boolean from mock data
 * so the UI doesn't go dark.
 */

import { useAuthStore } from '@/features/auth/store/auth.store'
import { usePresence, type PresenceStatus } from '../store/presence.store'
import { formatLastSeen } from '../utils/relativeTime'

interface MinimalConversation {
  isOnline?: boolean
  isGroup?: boolean
  participants?: { id: string }[]
}

/** Pick the *other* participant in a 1:1 DM. Returns null for groups / unknowns. */
function pickOtherParticipantId(c: MinimalConversation, myUserId: string | undefined): string | null {
  if (c.isGroup) return null
  const list = c.participants ?? []
  const other = list.find((p) => p.id !== myUserId)
  return other?.id ?? null
}

/**
 * Resolve "is this conversation's other party online?" — preferring live
 * presence data when available, falling back to the seeded `isOnline` field
 * otherwise.
 */
export function useConversationOnline(c: MinimalConversation): boolean {
  // Participant ids are contactPresenceId — filter self out with the presence id.
  const myId = useAuthStore((s) => s.presenceId)
  const otherId = pickOtherParticipantId(c, myId)
  // Hook must be called unconditionally; pass empty string for groups and
  // let the store hand back the offline placeholder.
  const live = usePresence(otherId ?? '')

  if (!otherId) return Boolean(c.isOnline)
  return live.status === 'online'
}

/**
 * Full effective presence status for the conversation's other party
 * (`online` / `away` / `busy` / `dnd` / `offline`), preferring live presence and
 * falling back to the seeded `isOnline` boolean. Use this instead of
 * `useConversationOnline` wherever away/busy/dnd must be reflected — a bare
 * `status === 'online'` check collapses every non-online status into "offline".
 */
export function useConversationStatus(c: MinimalConversation): PresenceStatus {
  // Participant ids are contactPresenceId — filter self out with the presence id.
  const myId = useAuthStore((s) => s.presenceId)
  const otherId = pickOtherParticipantId(c, myId)
  const live = usePresence(otherId ?? '')

  if (!otherId) return c.isOnline ? 'online' : 'offline'
  return live.status
}

/**
 * Returns a formatted "last seen N minutes ago" string for the conversation's
 * other party, or an empty string if unknown / unavailable.
 */
export function useConversationLastSeen(c: MinimalConversation, locale = 'en'): string {
  // Participant ids are contactPresenceId — filter self out with the presence id.
  const myId = useAuthStore((s) => s.presenceId)
  const otherId = pickOtherParticipantId(c, myId)
  const live = usePresence(otherId ?? '')
  if (!otherId || !live.lastSeenAt) return ''
  return formatLastSeen(live.lastSeenAt, Date.now(), locale)
}

/**
 * The other party's custom status message ("In a meeting", "On holiday", …),
 * or an empty string when they have none / this is a group.
 *
 * The message was already arriving in presence meta and landing in the store —
 * nothing rendered it, so setting a status appeared to do nothing to everyone
 * except the person who set it.
 */
export function useConversationCustomMessage(c: MinimalConversation): string {
  // Participant ids are contactPresenceId — filter self out with the presence id.
  const myId = useAuthStore((s) => s.presenceId)
  const otherId = pickOtherParticipantId(c, myId)
  const live = usePresence(otherId ?? '')
  if (!otherId) return ''
  return live.customMessage ?? ''
}

/**
 * True when the conversation's other party has turned off "Let others see
 * your status". Always false for a group and for the seeded-only fallback,
 * since that path predates the flag and never carried it.
 */
export function useConversationHidden(c: MinimalConversation): boolean {
  // Participant ids are contactPresenceId, filter self out with the presence id.
  const myId = useAuthStore((s) => s.presenceId)
  const otherId = pickOtherParticipantId(c, myId)
  const live = usePresence(otherId ?? '')
  if (!otherId) return false
  return live.hidden
}
